import { Component, Input } from '@angular/core';

export type CardNetwork =
  | 'amex'
  | 'discover'
  | 'jcb'
  | 'mastercard'
  | 'unionpay'
  | 'visa'
  | 'migo'
  | 'placeholder';

export const imagePaths = {
  amex: 'assets/img/card-brands/americanexpress.svg',
  discover: 'assets/img/card-brands/discover.svg',
  jcb: 'assets/img/card-brands/jcb.svg',
  mastercard: 'assets/img/card-brands/mastercard.svg',
  unionpay: 'assets/img/card-brands/unionpay.svg',
  visa: 'assets/img/card-brands/visa-blue.svg',
  'visa-white': 'assets/img/card-brands/visa-white.svg',
  migo: 'assets/img/card-brands/placeholder-cardbrand.svg',
  placeholder: 'assets/img/card-brands/card-placeholder.svg',
};

/**
 * Per-brand visual override. Use `key: 'global'` to apply the theme to every card brand
 * at once. `background` and `textColor` are each ignored unless set to a non-empty value.
 */
export interface CardBrandTheme {
  key: CardNetwork | 'global';
  background?: string;
  /** Text color for this brand (alias, number, name, date). */
  textColor?: string;
}

/** Optional client-driven customization for the payment card. Absent = default appearance. */
export interface CardConfig {
  cobrandLogo?: string;
  background?: string;
  /** Text color applied across the whole card (alias, number, name, date). Empty = per-brand default. */
  textColor?: string;
  cardBrandTheme?: CardBrandTheme[];
}

/**
 * Brand spellings the tokenization service can report, keyed by slug
 * (lowercased, non-letters stripped).
 *
 * `CardToken.type` is not an enum: the backend fills it from a per-processor
 * lookup table (`ProcessorsConfig.tokenizationCardNames`) and stores unmapped
 * brands verbatim, so one network arrives under several spellings — `amex`
 * (visa-cybersource), `american-express` (adyen, fac-2, mercadoPago) and
 * `American Express` (lookup miss) are all the same card.
 */
const CARD_BRAND_SLUG_ALIASES: Record<string, CardNetwork> = {
  amex: 'amex',
  americanexpress: 'amex',
  mastercard: 'mastercard',
  master: 'mastercard',
  mc: 'mastercard',
  visa: 'visa',
  discover: 'discover',
  jcb: 'jcb',
  unionpay: 'unionpay',
};

/**
 * Neutral tile used whenever the brand cannot be resolved, so an unknown
 * network degrades to a placeholder instead of an empty `<img>`. Diners Club
 * lands here today: the backend emits it but there is no brand tile for it yet.
 */
export const FALLBACK_CARD_NETWORK: CardNetwork = 'placeholder';

export const amexRegex = /^3[47][0-9]{2,}$/;
export const mcRegex =
  /^5[1-5][0-9]{5,}$|^222[1-9][0-9]{3,}$|^22[3-9][0-9]{4,}$|^2[3-6][0-9]{5,}$|^27[01][0-9]{4,}$|^2720[0-9]{3,}$/;
export const visaRegex = /^4[0-9]{1,}$/;
export const discoverRegex = /^6(?:011|5[0-9]{2})[0-9]{3,}$/;
export const jcbRegex = /^(?:2131|1800|35\d{0,3})/;
export const unionpayRegex = /^(62[0-9]{14,17})$/;

/**
 * Network derived from the issuer BIN, or `null` when it is not conclusive.
 *
 * Only Amex and Visa are tested here on purpose: the remaining brand regexes in
 * this file are written for full PANs and never match a 6-digit BIN, so those
 * brands are left to the reported brand name instead of failing silently.
 */
function networkFromBin(bin?: string | null): CardNetwork | null {
  const digits = (bin ?? '').replace(/\D/g, '');

  if (!digits) return null;
  if (amexRegex.test(digits)) return 'amex';
  if (visaRegex.test(digits)) return 'visa';

  return null;
}

/**
 * Resolves the brand tile key for a tokenized card. The BIN wins when it is
 * conclusive because it is data; the reported brand name is the fallback because
 * its spelling varies per processor. Never returns an unmapped key.
 */
export function resolveCardNetwork(
  cardType?: string | null,
  bin?: string | null,
): CardNetwork {
  const fromBin = networkFromBin(bin);
  if (fromBin) return fromBin;

  const slug = (cardType ?? '').toLowerCase().replace(/[^a-z]/g, '');

  return CARD_BRAND_SLUG_ALIASES[slug] ?? FALLBACK_CARD_NETWORK;
}

/** Brand logo path for a tokenized card. Always a usable `src`. */
export function cardBrandLogo(
  cardType?: string | null,
  bin?: string | null,
): string {
  return imagePaths[resolveCardNetwork(cardType, bin)];
}

@Component({
  standalone: false,
  selector: 'm-payment-card',
  templateUrl: './payment-card.component.html',
  styleUrls: ['./payment-card.component.scss'],
})
export class PaymentCardComponent {
  @Input() alias: string | undefined = '';
  @Input() name: string | undefined = '';
  @Input() number: string | undefined = '';
  @Input() expirationMonth: string | undefined = '';
  @Input() expirationYear: string | undefined = '';
  @Input() hasAlias: boolean | undefined = false;
  @Input() cardLabel = 'Nombre en tarjeta'
  @Input() cardConfig?: CardConfig;

  /** URL of a background image that failed to load; used to fall back to the default look. */
  failedImageUrl: string | null = null;

  get cardLogo() {
    return imagePaths[this.cardBrand as CardNetwork];
  }

  /**
   * Custom text color for the current brand; null falls back to the per-brand ngClass defaults.
   * When the background image failed to load the card reverts fully to defaults, so no color is applied.
   */
  get textColor(): string | null {
    if (this.backgroundImageFailed) return null;
    return (
      (this.brandThemeTextColor ?? this.cardConfig?.textColor)?.trim() || null
    );
  }

  /**
   * Text color from cardConfig for the current brand. A brand-specific entry wins over a
   * `global` entry, which in turn applies to every brand. Entries without a color are ignored.
   */
  private get brandThemeTextColor(): string | undefined {
    const themes = this.cardConfig?.cardBrandTheme;
    if (!themes?.length) return undefined;

    const forBrand = themes.find(
      (theme) => theme.key === this.themeBrandKey && !!theme.textColor?.trim(),
    );
    if (forBrand) return forBrand.textColor;

    return themes.find(
      (theme) => theme.key === 'global' && !!theme.textColor?.trim(),
    )?.textColor;
  }

  get cobrandLogo() {
    if (this.cardConfig?.cobrandLogo?.trim()) {
      return this.cardConfig.cobrandLogo.trim();
    }

    const useDefault = this.cardBrand === 'migo' || this.cardBrand === 'amex';
    return useDefault
      ? 'assets/img/card-brands/cobrandCardDefault.svg'
      : 'assets/img/card-brands/cobrandCardDefaultWhite.svg';
  }

  get cardBrand() {
    if (!this.number) return 'migo';

    const bin = this.number.replace(/\D/g, '');

    switch (bin) {
      case bin.match(amexRegex)?.input:
        return 'amex';
      case bin.match(mcRegex)?.input:
        return 'mastercard';
      case bin.match(visaRegex)?.input:
        return 'visa-white';
      case bin.match(discoverRegex)?.input:
        return 'discover';
      case bin.match(jcbRegex)?.input:
        return 'jcb';
      case bin.match(unionpayRegex)?.input:
        return 'unionpay';
      default:
        return 'migo';
    }
  }

  /** Maps the internal brand to a public CardNetwork key (e.g. 'visa-white' -> 'visa'). */
  private get themeBrandKey(): CardNetwork {
    return this.cardBrand === 'visa-white'
      ? 'visa'
      : (this.cardBrand as CardNetwork);
  }

  /**
   * Background from cardConfig for the current brand. A brand-specific entry wins over a
   * `global` entry, which in turn applies to every brand. Entries without a background are ignored.
   */
  private get brandThemeBackground(): string | undefined {
    const themes = this.cardConfig?.cardBrandTheme;
    if (!themes?.length) return undefined;

    const forBrand = themes.find(
      (theme) => theme.key === this.themeBrandKey && !!theme.background?.trim(),
    );
    if (forBrand) return forBrand.background;

    return themes.find(
      (theme) => theme.key === 'global' && !!theme.background?.trim(),
    )?.background;
  }

  /** Resolved custom background (per-brand/global theme, else the global `background`), trimmed. */
  private get customBackground(): string | undefined {
    return (
      (this.brandThemeBackground ?? this.cardConfig?.background)?.trim() ||
      undefined
    );
  }

  /** True when the given background value points to an image resource rather than a color/gradient. */
  private isImageBackground(value: string): boolean {
    return (
      /^(https?:\/\/|\/|assets\/|data:image)/i.test(value) ||
      /\.(png|jpe?g|svg|gif|webp)(\?|$)/i.test(value)
    );
  }

  /** Wraps image URLs in url(); colors and gradients are passed through untouched. */
  private toCssBackground(value: string): string {
    return this.isImageBackground(value)
      ? `url('${value}') center / cover no-repeat`
      : value;
  }

  /** URL of the custom background when it is an image, so the template can preload it. */
  get customBackgroundImageUrl(): string | null {
    const value = this.customBackground;
    return value && this.isImageBackground(value) ? value : null;
  }

  /** Marks the current background image as failed so the card reverts to its default look. */
  onBackgroundImageError(): void {
    this.failedImageUrl = this.customBackgroundImageUrl;
  }

  /** True when the resolved custom background is an image whose load has failed. */
  private get backgroundImageFailed(): boolean {
    const custom = this.customBackground;
    return (
      !!custom &&
      this.isImageBackground(custom) &&
      this.failedImageUrl === custom
    );
  }

  get bgColor() {
    const custom = this.customBackground;
    if (custom && !this.backgroundImageFailed) {
      return this.toCssBackground(custom);
    }

    switch (this.cardBrand) {
      case 'amex':
        return `linear-gradient(135deg, #F1F3FA 0%, #E2E4EC 100%)`;
      case 'mastercard':
        return `linear-gradient(135deg, #595962 0%, #141415 100%)`;
      case 'visa-white':
        return `linear-gradient(135deg, #3773DE 0%, #15C 100%)`;
      case 'discover':
        return `linear-gradient(135deg, #FF8E59 0%, #E45716 100%)`;
      case 'jcb':
        return `linear-gradient(135deg, #FF535E 0%, #CB343D 100%)`;
      case 'unionpay':
        return `linear-gradient(135deg, #007B7E 0%, #162E50 100%)`;
      default:
        return `#F8F8F8`;
    }
  }

  get cardNumber() {
    const cleanNumber = this.number?.replace(/\D/g, '') ?? ''
    const spacing = '  '; 
    switch(this.cardBrand) {
      case 'amex':
        if (cleanNumber.length <= 4) {
          return cleanNumber;
        } else if (cleanNumber.length <= 10) {
          return `${cleanNumber.slice(0, 4)}${spacing}${cleanNumber.slice(4)}`;
        } else {
          return `${cleanNumber.slice(0, 4)}${spacing}${cleanNumber.slice(4, 10)}${spacing}${cleanNumber.slice(10, 15)}`;
        }
      case 'jcb': 
      if (cleanNumber.length <= 4) {
        return cleanNumber;
      } else if (cleanNumber.length <= 10) {
        return `${cleanNumber.slice(0, 4)}${spacing}${cleanNumber.slice(4)}`;
      } else {
        return `${cleanNumber.slice(0, 4)}${spacing}${cleanNumber.slice(4, 10)}${spacing}${cleanNumber.slice(10, 16)}`;
      }
      default: 
       return cleanNumber.replace(/(\d{4})(?=\d)/g, '$1 ');
    }
  }

  get getDisplayName(): string {
    if (!this.name?.trim() || /\d/.test(this.name)) {
      return this.cardLabel;
    }
    return this.name.trim();
  }

  get getDisplayAlias(): string {
    if (!this.alias?.trim() || /\d/.test(this.alias)) {
      return '';
    }
    return this.alias.trim();
  }

  get getFormattedMonth(): string {
    if (!this.expirationMonth || !/^\d+$/.test(this.expirationMonth.trim())) {
      return '• •';
    }
    return this.expirationMonth.trim();
  }

  get getFormattedYear(): string {
    if (!this.expirationYear || !/^\d+$/.test(this.expirationYear.trim())) {
      return '• • ';
    }
    return this.expirationYear.trim();
  }
}
